import React from 'react';
import { Helmet } from 'react-helmet';

export interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  noindex?: boolean;
  schemaData?: object;
  twitterCard?: 'summary' | 'summary_large_image';
  additionalMeta?: Array<{ name?: string; property?: string; content: string }>;
}

const MetaTags: React.FC<SEOProps> = ({
  title = "Fownd - AI-Powered Clinical Documentation for Physical Therapy",
  description = "Revolutionize your PT practice with AI-powered clinical documentation. Save time with automated SOAP notes and streamline patient care with Fownd's Notation platform.",
  canonical,
  ogImage = "/assets/images/home/fownd.png",
  ogType = "website",
  keywords = "AI SOAP notes, physical therapy documentation, AI clinical notes, automated SOAP notes, AI in physical therapy, AI services for EMR and HER systems",
  noindex = false,
  schemaData,
  twitterCard = "summary_large_image",
  additionalMeta = []
}) => {
  const siteUrl = "https://fownd.care";
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonical} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="Fownd" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* AI Discovery Meta */}
      <meta name="ai:topic" content="AI in physical therapy" />
      <meta name="ai:domain" content="healthcare technology" />
      <meta name="ai:expertise" content="clinical documentation automation" />
      
      {/* Additional Meta Tags */}
      {additionalMeta.map((meta, index) => (
        <meta 
          key={index}
          {...(meta.name && { name: meta.name })}
          {...(meta.property && { property: meta.property })}
          content={meta.content}
        />
      ))}
      
      {/* Structured Data */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default MetaTags; 